import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Linkedin, Mail, MapPin, Phone, MousePointer2, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const EMAIL = "sushma.jaykar08@gmail.com";
const PHONE = "+91 83691 06606";
const LINKEDIN = "https://www.linkedin.com/in/sushma-jaykar/";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-2xl border border-lav-300 bg-white/80 px-5 py-4 text-[15px] text-ink outline-none transition-all placeholder:text-mist/50 focus:border-viol focus:ring-2 focus:ring-viol/20";

const Nodes = ({ className }) => (
    <svg className={className} width="88" height="60" viewBox="0 0 88 60" fill="none" aria-hidden="true">
        <path d="M8 50 L36 24 L62 38 L82 8" stroke="#7047F5" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="3 5" />
        <circle cx="8" cy="50" r="4" fill="#7047F5" fillOpacity="0.5" />
        <circle cx="36" cy="24" r="4" fill="#FFD84D" />
        <circle cx="62" cy="38" r="4" fill="#7047F5" fillOpacity="0.5" />
        <circle cx="82" cy="8" r="4" fill="#7047F5" />
    </svg>
);

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", looking_for: "", message: "" });
    const [sending, setSending] = useState(false);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            toast.success("Email copied to clipboard");
        } catch {
            toast.error("Couldn't copy — email is " + EMAIL);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/inquiries`, form);
            toast.success("Message sent — I'll get back to you soon!");
            setForm({ name: "", email: "", company: "", looking_for: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please email me directly at " + EMAIL);
        } finally {
            setSending(false);
        }
    };

    const scrollToForm = (e) => {
        e.preventDefault();
        if (window.__lenis) window.__lenis.scrollTo("#contact-form", { offset: -110 });
        else document.querySelector("#contact-form")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section id="contact" className="relative overflow-hidden py-24 lg:py-32" data-testid="contact-section">
            <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-viol/15 blur-3xl" />
            <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-lav-200 opacity-80 blur-3xl" />
            <div className="pointer-events-none absolute left-1/3 top-1/2 h-72 w-72 rounded-full bg-sun-soft opacity-25 blur-3xl" />
            <div className="pointer-events-none absolute right-1/4 top-1/3 h-64 w-64 rounded-full bg-[#FFC7E8]/30 blur-3xl" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <div className="flex justify-center"><Eyebrow>Contact</Eyebrow></div>
                    <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Let's create something{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10">meaningful</span>
                            <span className="absolute bottom-1 left-0 z-0 h-3.5 w-full rounded-sm bg-sun" />
                        </span>{" "}
                        together.
                    </h2>
                    <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-mist">
                        Have a product, idea or design challenge in mind? I'd love to hear about it.
                    </p>
                    <a
                        href="#contact-form"
                        onClick={scrollToForm}
                        className="btn-primary-grad group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:-translate-y-0.5"
                        data-testid="contact-cta-talk"
                    >
                        Let's Talk
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                </Reveal>

                <div className="relative mx-auto mt-20 max-w-5xl">
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-viol/10 blur-3xl" />
                    <span className="pointer-events-none absolute -top-6 left-8 z-10 text-lg text-sun-deep" aria-hidden="true">✦</span>
                    <span className="pointer-events-none absolute -bottom-6 right-12 z-10 text-sm text-viol" aria-hidden="true">✦</span>
                    <Mail className="pointer-events-none absolute -top-8 right-1/4 z-10 hidden h-7 w-7 rotate-12 text-viol/30 lg:block" aria-hidden="true" />

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">
                        <Reveal delay={0} className="lg:col-span-7">
                            <div className="glass group relative flex h-full flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(112,71,245,0.22)] sm:p-10" data-testid="contact-email-card">
                                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-viol/10 blur-2xl transition-colors duration-500 group-hover:bg-viol/20" />
                                <Mail className="pointer-events-none absolute -bottom-6 -right-4 h-32 w-32 rotate-[-8deg] text-viol/[0.07]" aria-hidden="true" />
                                <div className="flex items-start justify-between">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-viol text-sun shadow-[0_12px_30px_rgba(112,71,245,0.35)] transition-transform duration-300 group-hover:-rotate-6">
                                        <Mail className="h-8 w-8" />
                                    </span>
                                    <span className="hand rotate-3 text-xl text-viol" aria-hidden="true">drop me a line ✦</span>
                                </div>
                                <div className="mt-10">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Email</p>
                                    <p className="mt-2 break-all text-xl font-extrabold text-ink sm:text-2xl">{EMAIL}</p>
                                    <button
                                        onClick={copyEmail}
                                        className="mt-4 flex w-fit items-center gap-2 rounded-full bg-white/90 px-5 py-2.5 text-sm font-bold text-viol ring-1 ring-lav-300 transition-all hover:bg-viol hover:text-white"
                                        data-testid="contact-copy-email-button"
                                    >
                                        <Copy className="h-3.5 w-3.5" /> Copy email
                                    </button>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.08} className="lg:col-span-5">
                            <div className="relative h-full">
                                <span className="hand pointer-events-none absolute -top-8 right-4 z-10 rotate-6 text-2xl text-viol" aria-hidden="true">say hi! ↗</span>
                                <svg className="pointer-events-none absolute -top-5 right-24 z-10 hidden lg:block" width="40" height="30" viewBox="0 0 40 30" fill="none" aria-hidden="true">
                                    <path d="M2 26 C 14 10, 26 6, 37 10 M 37 10 l -7 -2 M 37 10 l -2 7" stroke="#7047F5" strokeOpacity="0.6" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                                <a
                                    href={LINKEDIN}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="glass group relative flex h-full flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(112,71,245,0.22)]"
                                    data-testid="contact-linkedin-card"
                                >
                                    <Nodes className="pointer-events-none absolute bottom-4 right-4 opacity-60 transition-opacity duration-500 group-hover:opacity-100" />
                                    <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-viol text-sun shadow-[0_12px_30px_rgba(112,71,245,0.35)] transition-transform duration-300 group-hover:rotate-6">
                                        <Linkedin className="h-8 w-8" />
                                    </span>
                                    <div className="mt-10">
                                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">LinkedIn</p>
                                        <p className="mt-2 flex items-center gap-1.5 text-xl font-extrabold text-ink">
                                            View LinkedIn
                                            <ArrowUpRight className="h-5 w-5 text-viol transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                        </p>
                                        <p className="mt-1 text-sm text-mist">Let's grow the network</p>
                                    </div>
                                </a>
                            </div>
                        </Reveal>

                        <Reveal delay={0.16} className="lg:col-span-5">
                            <a
                                href={`tel:${PHONE.replace(/\s/g, "")}`}
                                className="glass group relative flex h-full flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(112,71,245,0.22)]"
                                data-testid="contact-phone-card"
                            >
                                <div className="pointer-events-none absolute -left-8 -bottom-8 h-32 w-32 rounded-full bg-sun-soft/40 blur-2xl" />
                                <div className="flex items-start justify-between">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-viol text-sun shadow-[0_12px_30px_rgba(112,71,245,0.35)] transition-transform duration-300 group-hover:-rotate-6">
                                        <Phone className="h-8 w-8" />
                                    </span>
                                    <span className="pointer-events-none text-lg text-sun-deep" aria-hidden="true">✦</span>
                                </div>
                                <div className="mt-10">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Phone</p>
                                    <p className="mt-2 text-xl font-extrabold text-ink sm:text-2xl">{PHONE}</p>
                                    <p className="mt-1 text-sm text-mist">Mon–Sat, always up for a chat</p>
                                </div>
                            </a>
                        </Reveal>

                        <Reveal delay={0.24} className="lg:col-span-7">
                            <div className="glass group relative flex h-full flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(112,71,245,0.22)] sm:p-10" data-testid="contact-location-card">
                                <MapPin className="pointer-events-none absolute -bottom-8 -right-6 h-36 w-36 rotate-12 text-viol/[0.07]" aria-hidden="true" />
                                <div className="pointer-events-none absolute right-16 top-8 hidden items-center gap-1 lg:flex" aria-hidden="true">
                                    <MousePointer2 className="h-4 w-4 fill-viol/60 text-viol/60" />
                                    <span className="rounded-full rounded-bl-sm bg-viol/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-viol">Mumbai</span>
                                </div>
                                <div className="flex items-start justify-between">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-viol text-sun shadow-[0_12px_30px_rgba(112,71,245,0.35)] transition-transform duration-300 group-hover:rotate-6">
                                        <MapPin className="h-8 w-8" />
                                    </span>
                                </div>
                                <div className="mt-10">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Location</p>
                                    <p className="mt-2 text-xl font-extrabold text-ink sm:text-2xl">Mumbai, India</p>
                                    <p className="mt-1 text-sm text-mist">Open to remote & on-site collaborations</p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <form
                        id="contact-form"
                        onSubmit={submit}
                        className="glass relative mx-auto mt-20 max-w-3xl rounded-[2.5rem] p-8 sm:p-12"
                        data-testid="contact-form"
                    >
                        <span className="pointer-events-none absolute -top-3 right-10 text-lg text-sun-deep" aria-hidden="true">✦</span>
                        <p className="hand text-3xl text-viol" aria-hidden="true">tell me everything ✦</p>
                        <p className="mt-2 text-sm text-mist">A few details and I'll get back to you within a day or two.</p>
                        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Name"
                                className={inputCls}
                                data-testid="contact-form-name"
                            />
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="Email"
                                className={inputCls}
                                data-testid="contact-form-email"
                            />
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <input
                                value={form.company}
                                onChange={(e) => setForm({ ...form, company: e.target.value })}
                                placeholder="Company / Organization"
                                className={inputCls}
                                data-testid="contact-form-company"
                            />
                            <select
                                value={form.looking_for}
                                onChange={(e) => setForm({ ...form, looking_for: e.target.value })}
                                className={`${inputCls} ${form.looking_for ? "" : "text-mist/50"}`}
                                data-testid="contact-form-looking-for"
                            >
                                <option value="" disabled>What are you looking for?</option>
                                <option>UI/UX Design</option>
                                <option>Product Design</option>
                                <option>UX Audit / Research</option>
                                <option>Full-time Role</option>
                                <option>Collaboration</option>
                                <option>Something else</option>
                            </select>
                        </div>
                        <textarea
                            required
                            rows={5}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Message"
                            className={`${inputCls} mt-5 resize-none`}
                            data-testid="contact-form-message"
                        />
                        <button
                            type="submit"
                            disabled={sending}
                            className="btn-primary-grad group mt-8 flex w-full items-center justify-center gap-2 rounded-full px-7 py-5 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                            data-testid="contact-form-submit"
                        >
                            {sending ? "Sending…" : "Let's Talk"}
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
